import { HttpClient } from '@angular/common/http';
import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { DvFileDragDropDirective } from '@devinco/ui/legacy';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, take } from 'rxjs';
import { AttachedFile, FileUploadModalData, UploadStatus } from './interfaces';
import { SnackBarConfig } from '@shared/constants/snackbar-config';
import { CommonModule } from '@angular/common';
import { DvButtonComponent, DvDialogContent } from '@devinco/ui/dds';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';

@Component({
  imports: [
    CommonModule,
    TranslateModule,
    DvFileDragDropDirective,
    DvButtonComponent,
  ],
  selector: 'app-file-upload-dialog',
  templateUrl: './file-upload-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./file-upload-dialog.component.scss'],
})
export class FileUploadDialogComponent extends DvDialogContent<
  FileUploadModalData,
  FileUploadDialogComponent
> {
  private http = inject(HttpClient);
  private snackBar = inject(DvSnackbarService);
  private translate = inject(TranslateService);

  hasBaseDropZoneOver = false;
  attachedFiles: AttachedFile[] = [];
  uploadStatus: typeof UploadStatus = UploadStatus;
  readyToAttachFilesNo: number = 0;
  isFileUploadInProgress = false;
  readonly fileInputId = 'upload-area-file-input';

  fileOverBase(isFileOverBase: boolean): void {
    this.hasBaseDropZoneOver = isFileOverBase;
  }

  onFileSelected(event: Event): void {
    const files: FileList = (event.target as unknown as { files: FileList })
      .files as FileList;
    Object.keys(files).forEach((_, index) => {
      this.attachedFiles.push({
        ...this.mapAttachmentStatus(files[index]),
      });
    });
    this.calculateReadyToAttachFiles();
  }

  dropped(fileList: FileList): void {
    Object.keys(fileList).forEach((_, index) => {
      this.attachedFiles.push(this.mapAttachmentStatus(fileList[index]));
    });
    this.calculateReadyToAttachFiles();
  }

  deleteFile(index: number): void {
    this.attachedFiles.splice(index, 1);
    this.calculateReadyToAttachFiles();
  }

  onConfirmClick(): void {
    this.isFileUploadInProgress = true;
    const requests$ = this.attachedFiles
      .filter(file => file.status === this.uploadStatus.Success)
      .map(successFile => this.constructAttachmentAddRequest(successFile.file));

    combineLatest(requests$)
      .pipe(take(1))
      .subscribe(attachmentIds => {
        this.dialogRef!.onClose(attachmentIds, 'confirm');
        this.snackBar.open(
          this.translate.instant('FileUpload.FileSaveSuccessful'),
          SnackBarConfig.Success
        );
        this.isFileUploadInProgress = false;
      });
  }

  onCancelClick(): void {
    this.dialogRef!.onClose(undefined, 'cancel');
  }

  browse(): void {
    document.getElementById(this.fileInputId)?.click();
  }

  private mapAttachmentStatus(attachment: File): AttachedFile {
    if (attachment.size === 0) {
      return { file: attachment, status: this.uploadStatus.Empty };
    } else if (attachment.size > 50 * 1024 * 1024) {
      return { file: attachment, status: this.uploadStatus.TooBig };
    } else {
      return { file: attachment, status: this.uploadStatus.Success };
    }
  }

  private calculateReadyToAttachFiles(): void {
    this.readyToAttachFilesNo = this.attachedFiles.filter(
      file => file.status === UploadStatus.Success
    ).length;
  }

  private constructAttachmentAddRequest(file: File): Observable<number> {
    const formData = new FormData();

    formData.append('FileData', file);
    formData.append('fileName', file.name);

    return this.http.post<number>(this.data.url, formData);
  }
}
